import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Search, Plus, Eye, Edit, Trash2, Tv, Radio, MonitorPlay, Save, UploadCloud, Image as ImageIcon, CheckCircle, XCircle, BarChart3, ListVideo, MonitorCheck, Youtube, Heart, PlayCircle, Archive, RotateCcw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link, useSearchParams } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { sortProgramsChronological, MediaProgram } from "@/data/mediaPrograms";
import { useEmissionsStore } from "@/data/emissionsStore";
import { useEventMetricsStore } from "@/data/eventMetricsStore";
import { getVideos, addVideo, toggleVideoPublish, deleteVideo, MediathequeVideo } from "@/data/mediathequeStore";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { getApiUrl } from "@/utils/apiConfig";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AdminEmissionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [pendingPartnerFilter, setPendingPartnerFilter] = useState("all");
  const [pendingMonthFilter, setPendingMonthFilter] = useState("all");
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [previewProg, setPreviewProg] = useState<MediaProgram | null>(null);
  
  const { emissions: programs, fetchEmissions, saveEmission, updateEmissionStatus, deleteEmission } = useEmissionsStore();
  const { metrics, views, fetchMetrics } = useEventMetricsStore();
  const [mediaVideos, setMediaVideos] = useState<MediathequeVideo[]>([]);
  const [newVideoTitle, setNewVideoTitle] = useState("");
  const [newVideoUrl, setNewVideoUrl] = useState("");

  // Sources Auto State
  const [autoSources, setAutoSources] = useState<any[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [newSourceName, setNewSourceName] = useState("");
  const [newSourceChannel, setNewSourceChannel] = useState("");
  const [newSourceYoutubeUrl, setNewSourceYoutubeUrl] = useState("");
  const [newSourceRssUrl, setNewSourceRssUrl] = useState("");
  const [newSourceKeywords, setNewSourceKeywords] = useState("");
  const [newSourceMediaLogo, setNewSourceMediaLogo] = useState("");
  const [newSourceDefaultPoster, setNewSourceDefaultPoster] = useState("");
  const [newSourcePresenter, setNewSourcePresenter] = useState("");

  const fetchSources = async () => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const res = await fetch(`${getApiUrl()}/emissions_sources.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setAutoSources(data.sources);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchEmissions(false);
    fetchMetrics();
    setMediaVideos(getVideos());
    fetchSources();
  }, [fetchEmissions, fetchMetrics]);

  // Form State for new emission
  const [newEmitType, setNewEmitType] = useState("tv");
  const [newEmitChannel, setNewEmitChannel] = useState("");
  const [newEmitFrTitle, setNewEmitFrTitle] = useState("");
  const [newEmitArTitle, setNewEmitArTitle] = useState("");
  const [newEmitZghTitle, setNewEmitZghTitle] = useState("");
  const [newEmitSubjectFr, setNewEmitSubjectFr] = useState("");
  const [newEmitSubjectAr, setNewEmitSubjectAr] = useState("");
  const [newEmitSubjectZgh, setNewEmitSubjectZgh] = useState("");
  const [newEmitHost, setNewEmitHost] = useState("");
  const [newEmitDate, setNewEmitDate] = useState("");
  const [newEmitTime, setNewEmitTime] = useState("");
  const [newEmitEndTime, setNewEmitEndTime] = useState("");
  const [newEmitPeriodicity, setNewEmitPeriodicity] = useState("hebdo");
  const [autoPeriodicity, setAutoPeriodicity] = useState(false);
  const [newEmitDays, setNewEmitDays] = useState<string[]>([]);
  const [autoPublish, setAutoPublish] = useState(false);
  const [newEmitDesc, setNewEmitDesc] = useState("");
  const [newEmitGuests, setNewEmitGuests] = useState("");
  const [newEmitChannelUrl, setNewEmitChannelUrl] = useState("");
  const [newEmitEmissionUrl, setNewEmitEmissionUrl] = useState("");
  const [newEmitReplayUrl, setNewEmitReplayUrl] = useState("");
  const [editingProgramId, setEditingProgramId] = useState<string | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [mediaLogoUrl, setMediaLogoUrl] = useState<string | null>(null);
  const [socialYoutube, setSocialYoutube] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialTiktok, setSocialTiktok] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialX, setSocialX] = useState("");
  const [socialWebsite, setSocialWebsite] = useState("");
  const [submitType, setSubmitType] = useState<"save" | "publish">("publish");
  const [importUrl, setImportUrl] = useState("");
  const [isImporting, setIsImporting] = useState(false);

  const resetForm = () => {
    setEditingProgramId(null);
    setNewEmitType("tv");
    setNewEmitChannel("");
    setNewEmitFrTitle("");
    setNewEmitArTitle("");
    setNewEmitZghTitle("");
    setNewEmitSubjectFr("");
    setNewEmitSubjectAr("");
    setNewEmitSubjectZgh("");
    setNewEmitHost("");
    setNewEmitDate("");
    setNewEmitTime("");
    setNewEmitEndTime("");
    setNewEmitPeriodicity("hebdo");
    setAutoPeriodicity(false);
    setNewEmitDays([]);
    setAutoPublish(false);
    setNewEmitDesc("");
    setNewEmitGuests("");
    setNewEmitChannelUrl("");
    setNewEmitEmissionUrl("");
    setNewEmitReplayUrl("");
    setPosterUrl(null);
    setMediaLogoUrl(null);
    setSocialYoutube("");
    setSocialFacebook("");
    setSocialTiktok("");
    setSocialInstagram("");
    setSocialX("");
    setSocialWebsite("");
  };

  const handleAddVideo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVideoTitle || !newVideoUrl) {
      toast.error("Veuillez remplir le titre et le lien YouTube.");
      return;
    }
    const added = addVideo(newVideoTitle, newVideoUrl);
    setMediaVideos(getVideos());
    setNewVideoTitle("");
    setNewVideoUrl("");
    if (!added.embedUrl) {
      toast.warning("Vidéo ajoutée, mais le lien YouTube semble invalide pour l'intégration.");
    } else {
      toast.success("Vidéo ajoutée à la médiathèque !");
    }
  };

  const handleToggleVideo = (id: string) => {
    toggleVideoPublish(id);
    setMediaVideos(getVideos());
  };

  const handleDeleteVideo = (id: string) => {
    deleteVideo(id);
    setMediaVideos(getVideos());
    toast.success("Vidéo supprimée.");
  };

  const handleEditClick = (prog: MediaProgram) => {
    setEditingProgramId(prog.id);
    setNewEmitType(prog.type);
    setNewEmitChannel(prog.channel);
    setNewEmitFrTitle(prog.frTitle || "");
    setNewEmitArTitle(prog.arTitle || "");
    setNewEmitZghTitle(prog.zghTitle || "");
    setNewEmitSubjectFr(prog.subject_fr || "");
    setNewEmitSubjectAr(prog.subject_ar || "");
    setNewEmitSubjectZgh(prog.subject_zgh || "");
    setNewEmitHost(prog.host_fr || "");
    
    const d = new Date(prog.dateIso);
    const dateStr = d.toISOString().split("T")[0];
    const timeStr = [d.getHours().toString().padStart(2, "0"), d.getMinutes().toString().padStart(2, "0")].join(":");
    
    const endD = new Date(d.getTime() + prog.durationMinutes * 60000);
    const endTimeStr = [endD.getHours().toString().padStart(2, "0"), endD.getMinutes().toString().padStart(2, "0")].join(":");

    setNewEmitDate(dateStr);
    setNewEmitTime(timeStr);
    setNewEmitEndTime(endTimeStr);
    setNewEmitPeriodicity(prog.periodicity);
    setAutoPeriodicity(prog.autoPeriodicity || false);
    setNewEmitDays(prog.daysOfWeek || []);
    setAutoPublish(prog.autoPublish || false);
    setNewEmitDesc(prog.description_fr || "");
    setNewEmitGuests(prog.guests_fr || "");
    setNewEmitChannelUrl(prog.liveUrl || "");
    setNewEmitEmissionUrl(prog.youtubeUrl || "");
    setNewEmitReplayUrl(prog.replayUrl || "");
    setPosterUrl(prog.posterUrl || null);
    setMediaLogoUrl(prog.mediaLogo || null);
    setSocialYoutube(prog.socialLinks?.youtube || "");
    setSocialFacebook(prog.socialLinks?.facebook || "");
    setSocialTiktok(prog.socialLinks?.tiktok || "");
    setSocialInstagram(prog.socialLinks?.instagram || "");
    setSocialX(prog.socialLinks?.x || "");
    setSocialWebsite(prog.socialLinks?.website || "");

    setIsAddMenuOpen(true);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setPosterUrl(reader.result as string);
        toast.success("Affiche convertie et téléchargée avec succès.");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaLogoUrl(reader.result as string);
        toast.success("Logo généré avec succès.");
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredPrograms = programs.filter(prog => {
    if (prog.status === "pending") return false; // Show only approved/rejected in active list? Or just approved.
    if (prog.status === "rejected") return false;

    const matchesSearch = prog.frTitle.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          prog.channel.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || prog.type === typeFilter;
    
    let matchesTime = true;
    const today = new Date();
    const progDate = new Date(prog.dateIso);
    if (timeFilter === "month") {
      matchesTime = progDate.getMonth() === today.getMonth() && progDate.getFullYear() === today.getFullYear();
    } else if (timeFilter === "year") {
      matchesTime = progDate.getFullYear() === today.getFullYear();
    }

    return matchesSearch && matchesType && matchesTime;
  }).sort(sortProgramsChronological);

  const draftPrograms = programs.filter(prog => prog.status === "draft").sort(sortProgramsChronological);

  const pendingPrograms = programs.filter(prog => prog.status === "pending").sort(sortProgramsChronological);

  const archivedPrograms = programs.filter(prog => prog.status === "archived").sort(sortProgramsChronological);

  const filteredPendingPrograms = pendingPrograms.filter(prog => {
    if (pendingPartnerFilter !== "all" && (prog.partnerName || prog.channel) !== pendingPartnerFilter) return false;
    if (pendingMonthFilter !== "all") {
      const d = new Date(prog.createdAt || prog.dateIso);
      if (`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` !== pendingMonthFilter) return false;
    }
    return true;
  });

  const exportPendingToCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,\uFEFF"; 
    csvContent += "Partenaire;Titre;Chaine;Sujet;Animateur;Date_Diffusion;Soumission;Modifie\n";
    filteredPendingPrograms.forEach(prog => {
      const row = [
        `"${prog.partnerName || prog.channel}"`,
        `"${prog.frTitle}"`,
        `"${prog.channel}"`,
        `"${prog.subject_fr || ''}"`,
        `"${prog.host_fr || ''}"`,
        `"${prog.dateFormatted_fr} ${prog.time}"`,
        `"${prog.createdAt || prog.dateIso}"`,
        prog.isModified ? "Oui" : "Non"
      ].join(";");
      csvContent += row + "\n";
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `emissions_attente_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const togglePublish = async (id: string) => {
    const prog = programs.find(p => p.id === id);
    if (prog) {
        const newStatus = !prog.published;
        const updates: Partial<MediaProgram> = { id, published: newStatus };
        if (newStatus && prog.status === 'draft') {
            updates.status = 'approved';
        }
        await saveEmission(updates);
        toast.success(`Émission ${newStatus ? 'publiée' : 'masquée'} avec succès.`);
    }
  };

  const handleRemove = async (id: string) => {
    await deleteEmission(id);
    toast.success(`Émission supprimée avec succès.`);
  };

  const handleModerate = async (id: string, action: "approve" | "reject") => {
    await updateEmissionStatus(id, action === "approve" ? "approved" : "rejected");
    toast.success(action === "approve" ? "Émission partenaire approuvée." : "Émission partenaire rejetée.");
    window.dispatchEvent(new Event("kayninAdsUpdated")); 
  };

  const handleArchive = async (id: string) => {
    await updateEmissionStatus(id, "archived");
    toast.success("Émission archivée avec succès.");
  };

  const handleRestore = async (id: string) => {
    await updateEmissionStatus(id, "approved");
    toast.success("Émission restaurée et remise en ligne.");
  };

  const resetSourceForm = () => {
    setEditingSourceId(null);
    setNewSourceName(""); 
    setNewSourceChannel(""); 
    setNewSourceYoutubeUrl(""); 
    setNewSourceRssUrl(""); 
    setNewSourceKeywords("");
    setNewSourceMediaLogo(""); 
    setNewSourceDefaultPoster(""); 
    setNewSourcePresenter("");
  };

  const handleEditSource = (src: any) => {
    setEditingSourceId(src.id);
    setNewSourceName(src.nom_emission || "");
    setNewSourceChannel(src.chaine || "");
    setNewSourceYoutubeUrl(src.youtube_url || "");
    setNewSourceRssUrl(src.rss_url || "");
    setNewSourceKeywords(src.keywords || "");
    setNewSourceMediaLogo(src.media_logo || "");
    setNewSourceDefaultPoster(src.default_poster || "");
    setNewSourcePresenter(src.presentateur || "");
    setIsAddSourceOpen(true);
  };

  const handleAddSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSourceName || (!newSourceYoutubeUrl && !newSourceRssUrl)) {
      return toast.error("Le nom et au moins une URL (YouTube ou RSS) sont obligatoires");
    }
    
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const bodyPayload = {
        nom_emission: newSourceName,
        chaine: newSourceChannel,
        youtube_url: newSourceYoutubeUrl,
        rss_url: newSourceRssUrl,
        keywords: newSourceKeywords,
        media_logo: newSourceMediaLogo,
        default_poster: newSourceDefaultPoster,
        presentateur: newSourcePresenter,
      };

      if (editingSourceId) {
        Object.assign(bodyPayload, { id: editingSourceId, is_active: 1 });
      }

      const res = await fetch(`${getApiUrl()}/emissions_sources.php`, {
        method: editingSourceId ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(editingSourceId ? "Source mise à jour" : "Source ajoutée");
        fetchSources();
        setIsAddSourceOpen(false);
        resetSourceForm();
      } else {
        toast.error(data.error || "Erreur");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    }
  };

  const handleDeleteSource = async (id: string) => {
    if (!confirm("Supprimer cette source ?")) return;
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      await fetch(`${getApiUrl()}/emissions_sources.php?id=${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success("Source supprimée");
      fetchSources();
    } catch (e) {}
  };

  const handleToggleSource = async (id: string) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      await fetch(`${getApiUrl()}/emissions_sources.php`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, toggle_active: true })
      });
      fetchSources();
    } catch (e) {}
  };

  const handleScanSources = async () => {
    setIsScanning(true);
    const toastId = toast.loading("Lancement du robot d'aspiration...");
    try {
      await fetch(`${getApiUrl()}/../cron/emissions_scraper.php`);
      toast.success("Scan terminé !", { id: toastId });
      fetchEmissions(false);
    } catch (e) {
      toast.error("Erreur lors du scan", { id: toastId });
    } finally {
      setIsScanning(false);
    }
  };

  const handleAddEmission = (e: React.FormEvent) => {
    e.preventDefault();
    if (!(newEmitFrTitle || newEmitArTitle || newEmitZghTitle) || !newEmitChannel || !newEmitDate || !newEmitTime || !newEmitEndTime) {
      toast.error("Veuillez remplir les champs obligatoires (Chaîne, Date, Heures et au moins un Titre)");
      return;
    }

    const startObj = new Date(`${newEmitDate}T${newEmitTime}:00`);
    let endObj = new Date(`${newEmitDate}T${newEmitEndTime}:00`);
    if (endObj < startObj) {
      endObj = new Date(endObj.getTime() + 24 * 60 * 60 * 1000); // extends past midnight
    }
    const calculatedDuration = Math.max(1, Math.round((endObj.getTime() - startObj.getTime()) / 60000));

    const isPublish = submitType === "publish";

    const programData: Partial<MediaProgram> = {
      ...(editingProgramId ? { id: editingProgramId } : {}),
      frTitle: newEmitFrTitle || newEmitArTitle || newEmitZghTitle,
      arTitle: newEmitArTitle || newEmitFrTitle,
      zghTitle: newEmitZghTitle || newEmitFrTitle,
      subject_fr: newEmitSubjectFr,
      subject_ar: newEmitSubjectAr,
      subject_zgh: newEmitSubjectZgh,
      channel: newEmitChannel,
      type: newEmitType as "tv" | "radio" | "web",
      periodicity: newEmitPeriodicity as any,
      autoPeriodicity: autoPeriodicity,
      daysOfWeek: newEmitPeriodicity === 'quotidien' ? newEmitDays : [],
      autoPublish: (newEmitPeriodicity === 'hebdo' || newEmitPeriodicity === 'quotidien') ? autoPublish : false,
      dateFormatted_fr: newEmitDate,
      dateFormatted_ar: newEmitDate,
      dateFormatted_zgh: newEmitDate,
      time: newEmitTime,
      dateIso: startObj.toISOString(),
      durationMinutes: calculatedDuration,
      guests_fr: newEmitGuests,
      guests_ar: newEmitGuests,
      guests_zgh: newEmitGuests,
      description_fr: newEmitDesc,
      description_ar: newEmitDesc,
      description_zgh: newEmitDesc,
      host_fr: newEmitHost,
      host_ar: newEmitHost,
      host_zgh: newEmitHost,
      liveUrl: newEmitChannelUrl,
      youtubeUrl: newEmitEmissionUrl,
      replayUrl: newEmitReplayUrl,
      posterUrl: posterUrl || undefined,
      mediaLogo: mediaLogoUrl || undefined,
      socialLinks: {
        youtube: socialYoutube || undefined,
        facebook: socialFacebook || undefined,
        tiktok: socialTiktok || undefined,
        instagram: socialInstagram || undefined,
        x: socialX || undefined,
        website: socialWebsite || undefined,
      },
      published: isPublish,
      status: isPublish ? "approved" : "draft",
    };

    saveEmission(programData).then((saved) => {
      if (saved) {
        if (editingProgramId) {
          toast.success(isPublish ? "Émission modifiée et publiée avec succès !" : "Émission modifiée et sauvegardée en brouillon !");
        } else {
          toast.success(isPublish ? "Émission ajoutée et publiée avec succès !" : "Émission sauvegardée en brouillon avec succès !");
        }
        setIsAddMenuOpen(false);
        resetForm();
      } else {
        toast.error("Erreur lors de l'enregistrement de l'émission.");
      }
    });
  };

  const getMediaIcon = (type: string) => {
    if (type === 'tv') return <Tv className="w-4 h-4 text-indigo-500" />;
    if (type === 'radio') return <Radio className="w-4 h-4 text-emerald-500" />;
    return <MonitorPlay className="w-4 h-4 text-orange-500" />;
  };

  // Stats generation
  const activePrograms = programs.filter(p => p.status === "approved" || p.status === undefined);
  const dataByType = [
    { name: 'TV', value: activePrograms.filter(p => p.type === 'tv').length },
    { name: 'Radio', value: activePrograms.filter(p => p.type === 'radio').length },
    { name: 'Web', value: activePrograms.filter(p => p.type === 'web').length },
  ].filter(d => d.value > 0);

  const channelMap = new Map<string, number>();
  activePrograms.forEach(p => {
    channelMap.set(p.channel, (channelMap.get(p.channel) || 0) + 1);
  });
  const dataByChannel = Array.from(channelMap.entries()).map(([name, count]) => ({ name, count })).sort((a,b) => b.count - a.count).slice(0, 5);

  const handleMagicImport = async () => {
    if (!importUrl) return;
    setIsImporting(true);
    try {
      const { getApiUrl } = await import('@/utils/apiConfig');
      const res = await fetch(`${getApiUrl()}/magic_import.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: importUrl })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      
      setNewEmitFrTitle(data.title || newEmitFrTitle);
      setNewEmitDesc(data.description || newEmitDesc);
      if (data.image) setPosterUrl(data.image);
      if (!newEmitDate) setNewEmitDate(new Date().toISOString().split('T')[0]);
      
      setImportUrl("");
      toast.success("Données importées avec succès !");
    } catch (err: any) {
      toast.error("Erreur d'importation : " + err.message);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Gestion des Émissions</h1>
          <p className="text-muted-foreground font-body">Administration du portail audiovisuel (Validation & Ajout)</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Dialog open={isAddMenuOpen} onOpenChange={(open) => {
            setIsAddMenuOpen(open);
            if (!open) resetForm();
          }}>
            <Button className="gap-2 shrink-0 bg-primary hover:bg-primary/90 text-white font-bold" onClick={() => { resetForm(); setIsAddMenuOpen(true); }}>
              <Plus className="w-4 h-4" /> Ajouter une émission
            </Button>
            <DialogContent className="max-w-3xl overflow-y-auto max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{editingProgramId ? "Modifier l'émission" : "Ajouter une émission (Manuel / Automatique)"}</DialogTitle>
              <DialogDescription>
                {editingProgramId ? "Mettez à jour les informations de cette émission." : "Créez une nouvelle fiche d'émission ou importez les données via une URL (ex: Hespress, MAP...)."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddEmission} className="space-y-4 py-4">
              <div className="bg-muted/30 p-3 rounded-lg mb-4 flex flex-col gap-2">
                <Label className="font-body text-xs text-muted-foreground">Importer depuis un article ou une URL YouTube</Label>
                <div className="flex gap-2 items-center">
                  <Input placeholder="https://..." value={importUrl} onChange={(e) => setImportUrl(e.target.value)} className="font-body text-sm h-9" />
                  <Button type="button" size="sm" onClick={handleMagicImport} disabled={isImporting || !importUrl} className="shrink-0 h-9 font-body">
                    {isImporting ? "Importation..." : "✨ Import Magique"}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Support Média *</Label>
                  <Select value={newEmitType} onValueChange={setNewEmitType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tv">Télévision</SelectItem>
                      <SelectItem value="radio">Radio</SelectItem>
                      <SelectItem value="web">Web TV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nom de la chaîne / média *</Label>
                  <Input value={newEmitChannel} onChange={e => setNewEmitChannel(e.target.value)} placeholder="Ex: Al Aoula, 2M, Hit Radio..." required />
                </div>
              </div>

              <div className="bg-primary/5 p-3 rounded-lg border border-primary/20 space-y-4">
                <p className="text-xs font-semibold text-primary">Le titre doit être renseigné dans au moins l'une des langues suivantes :</p>
                <div className="space-y-2">
                  <Label>Titre de l'émission (Français)</Label>
                  <Input value={newEmitFrTitle} onChange={e => setNewEmitFrTitle(e.target.value)} placeholder="Ex: Le Débat" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Titre (Arabe)</Label>
                    <Input value={newEmitArTitle} onChange={e => setNewEmitArTitle(e.target.value)} placeholder="النقاش" dir="rtl" />
                  </div>
                  <div className="space-y-2">
                    <Label>Titre (Tamazight)</Label>
                    <Input value={newEmitZghTitle} onChange={e => setNewEmitZghTitle(e.target.value)} placeholder="ⴰⵙⵇⵙⵉ" />
                  </div>
                </div>
              </div>

              <div className="bg-muted/10 p-3 rounded-lg border border-border/50 space-y-4">
                <p className="text-xs font-semibold text-muted-foreground">Sujet / Titre de l'épisode du jour :</p>
                <div className="space-y-2">
                  <Label>Sujet de l'émission (Français)</Label>
                  <Input value={newEmitSubjectFr} onChange={e => setNewEmitSubjectFr(e.target.value)} placeholder="Ex: Élections 2026 - Le face à face" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sujet (Arabe)</Label>
                    <Input value={newEmitSubjectAr} onChange={e => setNewEmitSubjectAr(e.target.value)} placeholder="انتخابات 2026" dir="rtl" />
                  </div>
                  <div className="space-y-2">
                    <Label>Sujet (Tamazight)</Label>
                    <Input value={newEmitSubjectZgh} onChange={e => setNewEmitSubjectZgh(e.target.value)} placeholder="ⵉⵙⵜⴰⵢⵏ 2026" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Nom de l'animateur / présentateur</Label>
                <Input value={newEmitHost} onChange={e => setNewEmitHost(e.target.value)} placeholder="Ex: Tarik Yahya" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Date prévue *</Label>
                  <Input type="date" value={newEmitDate} onChange={e => setNewEmitDate(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Heure début *</Label>
                  <Input type="time" value={newEmitTime} onChange={e => setNewEmitTime(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Heure fin *</Label>
                  <Input type="time" value={newEmitEndTime} onChange={e => setNewEmitEndTime(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Périodicité</Label>
                  <Select value={newEmitPeriodicity} onValueChange={setNewEmitPeriodicity}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quotidien">Quotidienne</SelectItem>
                      <SelectItem value="hebdo">Hebdomadaire</SelectItem>
                      <SelectItem value="mensuel">Mensuelle</SelectItem>
                      <SelectItem value="ponctuel">Ponctuelle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {newEmitPeriodicity !== 'ponctuel' && (
                <div className="flex items-center justify-between space-x-2 bg-secondary/20 p-3 rounded-lg border border-secondary/40">
                  <div>
                    <Label htmlFor="auto-periodicity" className="font-semibold text-primary">Périodicité Auto (Renouvellement automatique)</Label>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Une fois la date de diffusion passée, le système calculera et mettra à jour automatiquement la date pour la prochaine diffusion.</p>
                  </div>
                  <Switch id="auto-periodicity" checked={autoPeriodicity} onCheckedChange={setAutoPeriodicity} />
                </div>
              )}

              {newEmitPeriodicity === 'quotidien' && (
                <div className="space-y-3 bg-muted/20 p-3 rounded-lg border border-border">
                  <Label>Jours de diffusion</Label>
                  <div className="flex flex-wrap gap-4">
                    {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map(day => (
                      <div key={day} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`day-${day}`} 
                          checked={newEmitDays.includes(day)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewEmitDays([...newEmitDays, day]);
                            } else {
                              setNewEmitDays(newEmitDays.filter(d => d !== day));
                            }
                          }}
                        />
                        <label htmlFor={`day-${day}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {day}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(newEmitPeriodicity === 'hebdo' || newEmitPeriodicity === 'quotidien') && (
                <div className="flex items-center space-x-2 bg-primary/5 p-3 rounded-lg border border-primary/20">
                  <Switch 
                    id="auto-publish" 
                    checked={autoPublish} 
                    onCheckedChange={setAutoPublish} 
                  />
                  <Label htmlFor="auto-publish" className="font-semibold text-primary cursor-pointer">
                    Activer la mise en ligne automatique
                  </Label>
                  <span className="text-xs text-muted-foreground ml-2">(Publie automatiquement l'émission en liste d'attente à la date/heure prévue)</span>
                </div>
              )}

              <div className="space-y-2">
                <Label>Invités / Intervenants</Label>
                <Input value={newEmitGuests} onChange={e => setNewEmitGuests(e.target.value)} placeholder="Ex: Ministre, Analyste..." />
              </div>

              <div className="space-y-2">
                <Label>Sujet / Description courte</Label>
                <Textarea value={newEmitDesc} onChange={e => setNewEmitDesc(e.target.value)} rows={3} placeholder="De quoi sera-t-il question..." />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Lien du Direct (Optionnel)</Label>
                  <Input type="url" value={newEmitChannelUrl} onChange={e => setNewEmitChannelUrl(e.target.value)} placeholder="https://..." />
                </div>
                <div className="space-y-2">
                  <Label>Lien YouTube (Optionnel)</Label>
                  <Input type="url" value={newEmitEmissionUrl} onChange={e => setNewEmitEmissionUrl(e.target.value)} placeholder="https://youtube.com/..." />
                </div>
                <div className="space-y-2">
                  <Label>Lien Replay/VOD (Optionnel)</Label>
                  <Input type="url" value={newEmitReplayUrl} onChange={e => setNewEmitReplayUrl(e.target.value)} placeholder="https://..." />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2 mt-4 border border-dashed border-border rounded-lg p-4 bg-muted/20">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center text-muted-foreground"><ImageIcon className="w-4 h-4 mr-2" /> Affiche du programme</Label>
                    {posterUrl && (
                       <Button type="button" variant="ghost" size="sm" onClick={() => setPosterUrl(null)} className="h-6 text-xs text-destructive">Retirer</Button>
                    )}
                  </div>
                  
                  {posterUrl ? (
                    <div className="relative rounded overflow-hidden aspect-video max-w-[200px] border border-border mt-2 bg-muted/20">
                      <img src={posterUrl} alt="Affiche" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="space-y-3 mt-2">
                      <Input 
                        type="url" 
                        placeholder="URL de l'image (ex: https://...)" 
                        value={posterUrl || ""} 
                        onChange={(e) => setPosterUrl(e.target.value)} 
                        className="text-xs h-8"
                      />
                      <div className="flex items-center gap-2">
                        <div className="h-px bg-border flex-1"></div>
                        <span className="text-[10px] text-muted-foreground uppercase font-bold">OU</span>
                        <div className="h-px bg-border flex-1"></div>
                      </div>
                      <div 
                        className="w-full h-12 rounded border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <UploadCloud className="w-4 h-4 mb-1 opacity-60" />
                        <span className="text-xs font-medium">Uploader depuis l'ordinateur</span>
                      </div>
                    </div>
                  )}
                  
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    className="hidden" 
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </div>

                <div className="space-y-2 mt-4 border border-dashed border-border rounded-lg p-4 bg-muted/20">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center text-muted-foreground"><ImageIcon className="w-4 h-4 mr-2" /> Logo Média (Auto-généré ou upload)</Label>
                    {mediaLogoUrl && (
                       <Button type="button" variant="ghost" size="sm" onClick={() => setMediaLogoUrl(null)} className="h-6 text-xs text-destructive">Retirer</Button>
                    )}
                  </div>
                  
                  {mediaLogoUrl ? (
                    <div className="relative rounded overflow-hidden aspect-square max-w-[80px] border border-border mt-2 bg-white">
                      <img src={mediaLogoUrl} alt="Logo" className="w-full h-full object-contain p-2" />
                    </div>
                  ) : (
                    <div className="space-y-3 mt-2">
                      <Input 
                        type="url" 
                        placeholder="URL du logo (ex: https://...)" 
                        value={mediaLogoUrl || ""} 
                        onChange={(e) => setMediaLogoUrl(e.target.value)} 
                        className="text-xs h-8"
                      />
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => handleLogoChange({target: {files: [new File([""], "logo.png")]}} as any)} className="flex-1 text-xs h-8">Générer Auto</Button>
                        <div 
                          className="w-full h-8 flex-1 rounded border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => logoInputRef.current?.click()}
                        >
                          <span className="text-xs font-medium">Uploader Logo</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <input 
                    type="file" 
                    ref={logoInputRef}
                    className="hidden" 
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </div>
              </div>

              <div className="space-y-2 mt-4">
                <Label>Réseaux Sociaux et Site Web du média (Facultatifs)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 bg-muted/10 p-4 rounded-lg border border-border/50">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Site Web (Direct)</Label>
                    <Input value={socialWebsite} onChange={e => setSocialWebsite(e.target.value)} placeholder="https://..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">YouTube</Label>
                    <Input value={socialYoutube} onChange={e => setSocialYoutube(e.target.value)} placeholder="https://youtube.com/..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Facebook</Label>
                    <Input value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} placeholder="https://facebook.com/..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Instagram</Label>
                    <Input value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} placeholder="https://instagram.com/..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">TikTok</Label>
                    <Input value={socialTiktok} onChange={e => setSocialTiktok(e.target.value)} placeholder="https://tiktok.com/..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">X</Label>
                    <Input value={socialX} onChange={e => setSocialX(e.target.value)} placeholder="https://x.com/..." className="h-8 text-xs" />
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-6 flex flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={() => setIsAddMenuOpen(false)}>Annuler</Button>
                <div className="flex gap-2">
                   <Button type="submit" variant="secondary" onClick={() => setSubmitType("save")} className="flex-1 sm:flex-none whitespace-nowrap"><Save className="w-4 h-4 mr-2" /> Sauvegarder</Button>
                   <Button type="submit" onClick={() => setSubmitType("publish")} className="flex-1 sm:flex-none whitespace-nowrap"><CheckCircle className="w-4 h-4 mr-2" /> {editingProgramId ? "Publier" : "Enregistrer & Publier"}</Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      <Tabs value={searchParams.get("tab") || "active"} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="mb-6 h-auto bg-muted/50 p-1 flex flex-wrap rounded-xl w-full max-w-2xl gap-1">
          <TabsTrigger value="active" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm">
            <ListVideo className="w-4 h-4" /> Programmes Actifs
          </TabsTrigger>
          <TabsTrigger value="pending" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm">
            <MonitorCheck className="w-4 h-4" /> Demandes Médias
            {pendingPrograms.length > 0 && (
              <Badge className="bg-destructive text-destructive-foreground ml-2 px-1.5 py-0 min-w-[1.25rem] h-5">{pendingPrograms.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="stats" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm">
            <BarChart3 className="w-4 h-4" /> Statistiques
          </TabsTrigger>
          <TabsTrigger value="brouillons" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm text-amber-700">
            <Save className="w-4 h-4" /> Brouillons
            {draftPrograms.length > 0 && (
              <Badge className="bg-amber-100 text-amber-800 ml-2 px-1.5 py-0 min-w-[1.25rem] h-5">{draftPrograms.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="archives" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm text-slate-600">
            <Archive className="w-4 h-4" /> Archives
          </TabsTrigger>
          <TabsTrigger value="mediatheque" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm">
            <Youtube className="w-4 h-4" /> Médiathèque
          </TabsTrigger>
          <TabsTrigger value="sources" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm text-blue-700 bg-blue-50/50">
            <Radio className="w-4 h-4" /> Sources Auto
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-0">
          <Card>
            <CardHeader className="pb-4 border-b">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex flex-1 gap-4 w-full">
                  <div className="relative w-full md:max-w-sm">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher une émission ou chaîne..."
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Tous les supports" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les supports</SelectItem>
                      <SelectItem value="tv">Télévision</SelectItem>
                      <SelectItem value="radio">Radio</SelectItem>
                      <SelectItem value="web">Web TV</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={timeFilter} onValueChange={setTimeFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Période" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les dates</SelectItem>
                      <SelectItem value="month">Ce Mois</SelectItem>
                      <SelectItem value="year">Cette Année</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/30">
                    <TableRow>
                      <TableHead className="w-[40px] px-4"><Checkbox /></TableHead>
                      <TableHead>Chaîne / Média</TableHead>
                      <TableHead>Titre de l'émission</TableHead>
                      <TableHead>Animateur & Liens</TableHead>
                      <TableHead>Date & Périodicité</TableHead>
                      <TableHead>Visibilité</TableHead>
                      <TableHead className="text-center">Vues</TableHead>
                      <TableHead className="text-center">Favoris</TableHead>
                      <TableHead className="text-right px-4">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPrograms.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                          Aucune émission active trouvée.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPrograms.map((prog) => (
                        <TableRow key={prog.id} className={!prog.published ? "bg-muted/30 opacity-70" : ""}>
                          <TableCell className="px-4"><Checkbox /></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getMediaIcon(prog.type)}
                              <span className="font-bold text-foreground">{prog.channel}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <button type="button" onClick={() => setPreviewProg(prog)} className="font-medium text-primary hover:underline text-left line-clamp-1 bg-transparent border-0 p-0 m-0">
                              {prog.frTitle}
                            </button>
                            {prog.description_fr && <div className="text-xs text-muted-foreground line-clamp-1 max-w-[200px] mt-1">{prog.description_fr}</div>}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-semibold">{prog.host_fr || "N/A"}</div>
                            <div className="text-[10px] text-muted-foreground flex gap-1 mt-1">
                               {prog.replayUrl ? <Badge variant="secondary" className="px-1 py-0 h-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Replay dispo</Badge> : null}
                               {prog.liveUrl ? <Badge variant="secondary" className="px-1 py-0 h-4 bg-blue-100 text-blue-700 hover:bg-blue-200">Live dispo</Badge> : null}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium">
                              {prog.dateFormatted_fr} à {prog.time}
                            </div>
                            <Badge variant="outline" className="capitalize text-[10px] mt-1 flex items-center gap-1 w-fit">
                              {prog.periodicity}
                              {prog.autoPeriodicity && <RotateCcw className="w-3 h-3 text-emerald-500" title="Renouvellement automatique activé" />}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              {prog.status === 'draft' && (
                                <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 self-start mb-1">
                                  Brouillon
                                </Badge>
                              )}
                              <Switch 
                                checked={prog.published}
                                onCheckedChange={() => togglePublish(prog.id)}
                              />
                              <span className="text-[10px] text-muted-foreground font-medium">
                                {prog.published ? "En ligne" : "Masqué"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="bg-blue-50 text-blue-600 hover:bg-blue-100 font-bold border-blue-200">
                              <Eye className="w-3 h-3 mr-1.5" /> {views[prog.id] || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="bg-pink-50 text-pink-600 hover:bg-pink-100 font-bold border-pink-200">
                              <Heart className="w-3 h-3 mr-1.5 fill-pink-500" /> {metrics[prog.id] || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right px-4">
                            <div className="flex justify-end gap-1">
                              {prog.liveUrl && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => window.open(prog.liveUrl, '_blank')} title="Rejoindre le direct">
                                  <PlayCircle className="h-4 w-4" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-blue-600" onClick={() => handleEditClick(prog)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-slate-600" onClick={() => handleArchive(prog.id)} title="Archiver l'émission">
                                <Archive className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => handleRemove(prog.id)} title="Supprimer définitivement">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="brouillons" className="mt-0">
          <Card>
            <CardHeader className="pb-4 border-b">
              <CardTitle className="text-xl text-amber-700">Programmes en Brouillon</CardTitle>
              <p className="text-sm text-muted-foreground">Ces émissions ont été sauvegardées mais pas encore publiées ou soumises.</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto min-h-[400px]">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/50 text-muted-foreground uppercase text-[10px] sm:text-xs">
                    <tr>
                      <th className="px-4 py-4 font-semibold w-12 text-center">Affiche</th>
                      <th className="px-4 py-4 font-semibold">Chaîne / Média</th>
                      <th className="px-4 py-4 font-semibold">Titre de l'émission</th>
                      <th className="px-4 py-4 font-semibold">Animateur</th>
                      <th className="px-4 py-4 font-semibold">Date & Périodicité</th>
                      <th className="px-4 py-4 font-semibold">Statut</th>
                      <th className="px-4 py-4 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {draftPrograms.map((prog) => (
                      <tr key={prog.id} className="hover:bg-muted/30 transition-colors group">
                        <td className="px-4 py-3 text-center">
                          {prog.posterUrl ? (
                            <div className="w-10 h-10 rounded overflow-hidden bg-muted mx-auto border">
                              <img src={prog.posterUrl} alt="Affiche" className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded bg-muted/50 mx-auto border flex items-center justify-center">
                              <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold flex items-center gap-2 text-primary">
                            {prog.mediaLogo ? (
                               <img src={prog.mediaLogo} alt="Logo" className="w-5 h-5 object-contain" />
                            ) : (
                               <Tv className="w-4 h-4 text-primary" />
                            )}
                            {prog.channel}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold text-foreground text-sm leading-tight max-w-[200px] truncate">
                            {prog.frTitle}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs font-semibold">
                          {prog.host_fr || '-'}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <div className="font-semibold text-foreground">
                            {prog.dateFormatted_fr} <br/> à {prog.time}
                          </div>
                          <Badge variant="outline" className="text-[9px] mt-1 text-muted-foreground bg-muted/20">{prog.periodicity}</Badge>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <Badge className="bg-amber-100 text-amber-800 border-none"><Save className="w-3 h-3 mr-1" /> Brouillon</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="outline" size="icon" className="h-8 w-8 text-primary border-primary/20 hover:bg-primary/10" onClick={() => handleEditClick(prog)} title="Modifier/Publier">
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="outline" size="icon" className="h-8 w-8 text-destructive border-destructive/20 hover:bg-destructive/10" onClick={() => handleRemove(prog.id)} title="Supprimer">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {draftPrograms.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground bg-muted/10">
                          Aucun brouillon trouvé.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="mt-0">
          <Card className="border-orange-200 shadow-sm">
            <CardHeader className="bg-orange-50/50 border-b border-orange-100">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl text-orange-800 flex items-center gap-2">
                    <MonitorCheck className="w-5 h-5" />
                    Validation des Suggestions Externes
                  </CardTitle>
                  <CardDescription>
                    Les médias partenaires ont soumis ces programmes pour diffusion sur votre plateforme électorale.
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={exportPendingToCSV} className="font-body text-xs border-green-200 text-green-700 hover:bg-green-50">
                  <UploadCloud className="w-4 h-4 mr-2" /> Exporter (CSV)
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Select value={pendingPartnerFilter} onValueChange={setPendingPartnerFilter}>
                  <SelectTrigger className="w-[160px] h-8 text-xs font-body"><SelectValue placeholder="Média/Partenaire" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les partenaires</SelectItem>
                    {Array.from(new Set(pendingPrograms.map(a => a.partnerName || a.channel))).map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={pendingMonthFilter} onValueChange={setPendingMonthFilter}>
                  <SelectTrigger className="w-[140px] h-8 text-xs font-body"><SelectValue placeholder="Mois de Soumission" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les mois</SelectItem>
                    {Array.from(new Set(pendingPrograms.map(a => {
                      const d = new Date(a.createdAt || a.dateIso);
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    }))).map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="px-6 py-4">Partenaire Média</TableHead>
                      <TableHead>Programme</TableHead>
                      <TableHead>Aperçu Description</TableHead>
                      <TableHead className="text-center">Dates</TableHead>
                      <TableHead className="text-right px-6">Modération</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPendingPrograms.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                          Aucune demande trouvée avec ces filtres.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPendingPrograms.map(prog => (
                        <TableRow key={prog.id} className="hover:bg-muted/10">
                          <TableCell className="px-6">
                            <div className="font-bold text-foreground">{prog.partnerName || prog.channel}</div>
                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1 uppercase font-semibold">
                              {getMediaIcon(prog.type)} {prog.type}
                            </div>
                            <div className="text-[10px] text-black font-bold mt-1">
                              Soumis le: {new Date(prog.createdAt || prog.dateIso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                            </div>
                            {prog.isModified && <Badge className="bg-red-500 text-white animate-pulse mt-1 text-[10px] py-0 border-0">MODIFIÉE</Badge>}
                          </TableCell>
                          <TableCell>
                            <div className="font-bold text-primary">{prog.frTitle}</div>
                            {prog.host_fr && <div className="text-xs">Animé par: {prog.host_fr}</div>}
                          </TableCell>
                          <TableCell>
                            {prog.isModified && prog.modificationReason && (
                               <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-800">
                                 <strong>Motif modification :</strong> {prog.modificationReason}
                               </div>
                            )}
                            <p className="text-xs text-muted-foreground line-clamp-2 max-w-sm">
                              {prog.description_fr}
                            </p>
                            {prog.guests_fr && <p className="text-[10px] italic mt-1 text-foreground/70">Invités: {prog.guests_fr}</p>}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="mb-1">{prog.dateFormatted_fr}</Badge>
                            <div className="text-xs">{prog.time} ({prog.durationMinutes} min)</div>
                          </TableCell>
                          <TableCell className="text-right px-6">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" className="text-destructive px-2 hover:bg-destructive hover:text-white" onClick={() => handleModerate(prog.id, "reject")} title="Rejeter">
                                <XCircle className="w-4 h-4" />
                              </Button>
                              {prog.liveUrl && (
                                <Button size="sm" variant="outline" className="text-destructive px-2 border-destructive hover:bg-destructive hover:text-white" onClick={() => window.open(prog.liveUrl, '_blank')} title="Rejoindre le direct">
                                  <PlayCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button size="sm" variant="outline" className="text-blue-600 px-2 border-blue-200 hover:bg-blue-50" onClick={() => handleEditClick(prog)} title="Modifier avant validation">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" onClick={() => handleModerate(prog.id, "approve")}>
                                <CheckCircle className="w-4 h-4" /> Valider
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Répartition par Support Médias</CardTitle>
                <CardDescription>Visualisation du volume de programmes par type (TV, Radio, Web).</CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                {dataByType.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={dataByType}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {dataByType.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">Pas de données.</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Médias les plus actifs (Top 5)</CardTitle>
                <CardDescription>Nombre de programmes approuvés par chaîne partenaire.</CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                {dataByChannel.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dataByChannel} layout="vertical" margin={{ top: 5, right: 30, left: 50, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={80} tick={{fontSize: 12}} />
                      <RechartsTooltip cursor={{fill: 'transparent'}} />
                      <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} barSize={24} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">Pas de données.</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="archives" className="mt-0">
          <Card>
            <CardHeader className="pb-4 border-b">
              <CardTitle className="text-xl text-slate-600 flex items-center gap-2">
                <Archive className="w-5 h-5" /> Archives des Émissions
              </CardTitle>
              <p className="text-sm text-muted-foreground">Ces émissions ont été archivées. Elles ne sont plus visibles au public mais conservent tous leurs détails.</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto min-h-[400px]">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/50 text-muted-foreground uppercase text-[10px] sm:text-xs">
                    <tr>
                      <th className="px-4 py-4 font-semibold w-12 text-center">Affiche</th>
                      <th className="px-4 py-4 font-semibold">Chaîne / Média</th>
                      <th className="px-4 py-4 font-semibold">Titre de l'émission</th>
                      <th className="px-4 py-4 font-semibold">Animateur</th>
                      <th className="px-4 py-4 font-semibold">Date de Diffusion</th>
                      <th className="px-4 py-4 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {archivedPrograms.length === 0 ? (
                       <tr>
                         <td colSpan={6} className="text-center py-8 text-muted-foreground">Aucune émission dans les archives.</td>
                       </tr>
                    ) : (
                      archivedPrograms.map((prog) => (
                        <tr key={prog.id} className="hover:bg-muted/30 transition-colors group opacity-80">
                          <td className="px-4 py-3 text-center">
                            {prog.posterUrl ? (
                              <div className="w-10 h-10 rounded overflow-hidden bg-muted mx-auto border filter grayscale">
                                <img src={prog.posterUrl} alt="Affiche" className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded bg-muted/50 mx-auto border flex items-center justify-center filter grayscale">
                                <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-bold flex items-center gap-2 text-slate-700">
                              {prog.mediaLogo ? (
                                 <img src={prog.mediaLogo} alt="Logo" className="w-5 h-5 object-contain filter grayscale" />
                              ) : (
                                 <Tv className="w-4 h-4 text-slate-500" />
                              )}
                              {prog.channel}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button type="button" onClick={() => setPreviewProg(prog)} className="font-bold text-foreground text-sm leading-tight max-w-[200px] truncate hover:underline bg-transparent border-0 p-0 m-0">
                              {prog.frTitle}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{prog.host_fr || "N/A"}</td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium">
                              {prog.dateFormatted_fr} à {prog.time}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50" onClick={() => handleRestore(prog.id)} title="Restaurer l'émission">
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => handleRemove(prog.id)} title="Supprimer définitivement">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mediatheque" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Youtube className="w-5 h-5 text-red-600" />
                La Médiathèque Kayn'in
              </CardTitle>
              <CardDescription>
                Ajoutez des liens YouTube pour qu'ils soient consultables directement depuis la page des émissions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddVideo} className="flex gap-4 mb-8 bg-muted/30 p-4 rounded-xl border border-border/50 items-end flex-wrap">
                <div className="space-y-2 flex-1 min-w-[200px]">
                  <Label>Titre de la vidéo *</Label>
                  <Input value={newVideoTitle} onChange={e => setNewVideoTitle(e.target.value)} placeholder="Ex: Le Débat - Spéciale Élections" />
                </div>
                <div className="space-y-2 flex-1 min-w-[300px]">
                  <Label>Lien YouTube *</Label>
                  <Input value={newVideoUrl} onChange={e => setNewVideoUrl(e.target.value)} placeholder="Ex: https://www.youtube.com/watch?v=..." />
                </div>
                <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white gap-2">
                  <Plus className="w-4 h-4" /> Ajouter à la médiathèque
                </Button>
              </form>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titre</TableHead>
                      <TableHead>Aperçu Lien</TableHead>
                      <TableHead className="text-center">Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mediaVideos.length === 0 ? (
                       <TableRow>
                         <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Aucune vidéo dans la médiathèque.</TableCell>
                       </TableRow>
                    ) : (
                      mediaVideos.map(vid => (
                        <TableRow key={vid.id} className={!vid.published ? "bg-muted/30 opacity-70" : ""}>
                          <TableCell className="font-bold">{vid.title}</TableCell>
                          <TableCell>
                            <a href={vid.youtubeUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                               <Youtube className="w-3 h-3" /> Voir sur YouTube
                            </a>
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch checked={vid.published} onCheckedChange={() => handleToggleVideo(vid.id)} />
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteVideo(vid.id)}>
                               <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

          <TabsContent value="sources" className="mt-0">
            <Card className="border-blue-200 shadow-sm">
              <CardHeader className="bg-blue-50/50 border-b border-blue-100 pb-4">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div>
                    <CardTitle className="text-xl text-blue-800 flex items-center gap-2">
                      <Radio className="w-5 h-5" /> Sources Automatiques (Générateur)
                    </CardTitle>
                    <CardDescription className="text-blue-700/70">
                      Configurez les flux YouTube (RSS) et flux RSS web pour aspirer automatiquement les nouvelles émissions. Elles atterriront en "Brouillon".
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="gap-2 bg-white" onClick={handleScanSources} disabled={isScanning}>
                      <RotateCcw className={`w-4 h-4 ${isScanning ? 'animate-spin text-blue-600' : 'text-blue-600'}`} /> Scanner Maintenant
                    </Button>
                    <Button className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setIsAddSourceOpen(true)}>
                      <Plus className="w-4 h-4" /> Ajouter Source
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="rounded-lg border overflow-hidden bg-white">
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow>
                        <TableHead>Statut</TableHead>
                        <TableHead>Nom Émission</TableHead>
                        <TableHead>Chaîne / Source</TableHead>
                        <TableHead>Liens Configurés</TableHead>
                        <TableHead>Mots Clés</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {autoSources.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            Aucune source automatique configurée.
                          </TableCell>
                        </TableRow>
                      ) : (
                        autoSources.map(src => (
                          <TableRow key={src.id}>
                            <TableCell>
                              <Switch checked={src.is_active === 1 || src.is_active === true} onCheckedChange={() => handleToggleSource(src.id)} />
                            </TableCell>
                            <TableCell className="font-semibold">{src.nom_emission}</TableCell>
                            <TableCell>{src.chaine}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {src.youtube_url && <Badge variant="outline" className="text-[10px] max-w-[150px] truncate" title={src.youtube_url}>YT: {src.youtube_url}</Badge>}
                                {src.rss_url && <Badge variant="outline" className="text-[10px] max-w-[150px] truncate text-orange-600 border-orange-200" title={src.rss_url}>RSS: {src.rss_url}</Badge>}
                                {!src.youtube_url && !src.rss_url && src.source_url && (
                                  <Badge variant="outline" className="text-[10px] max-w-[150px] truncate">{src.source_url}</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">{src.keywords || '-'}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => handleEditSource(src)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteSource(src.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Dialog open={isAddSourceOpen} onOpenChange={setIsAddSourceOpen}>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Ajouter une Source Auto</DialogTitle>
                  <DialogDescription>
                    Configurez l'aspiration automatique d'une émission via YouTube ou RSS.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddSource} className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom de l'émission ciblée</Label>
                      <Input required value={newSourceName} onChange={e => setNewSourceName(e.target.value)} placeholder="Ex: Fass a Fass" />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom de la chaîne Média</Label>
                      <Input value={newSourceChannel} onChange={e => setNewSourceChannel(e.target.value)} placeholder="Ex: Hespress" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>URL YouTube (RSS)</Label>
                      <Input type="url" value={newSourceYoutubeUrl} onChange={e => setNewSourceYoutubeUrl(e.target.value)} placeholder="https://www.youtube.com/feeds/videos.xml?channel_id=..." />
                    </div>
                    <div className="space-y-2">
                      <Label>URL Site Web (RSS)</Label>
                      <Input type="url" value={newSourceRssUrl} onChange={e => setNewSourceRssUrl(e.target.value)} placeholder="https://..." />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Présentateur par défaut</Label>
                    <Input value={newSourcePresenter} onChange={e => setNewSourcePresenter(e.target.value)} placeholder="Ex: Ridouane Erramdani" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>URL Logo Média (Optionnel)</Label>
                      <Input type="url" value={newSourceMediaLogo} onChange={e => setNewSourceMediaLogo(e.target.value)} placeholder="https://..." />
                    </div>
                    <div className="space-y-2">
                      <Label>URL Affiche par défaut (Optionnel)</Label>
                      <Input type="url" value={newSourceDefaultPoster} onChange={e => setNewSourceDefaultPoster(e.target.value)} placeholder="https://..." />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Mots Clés (Optionnel, séparés par virgule)</Label>
                    <Input value={newSourceKeywords} onChange={e => setNewSourceKeywords(e.target.value)} placeholder="Ex: politique, PJD, PAM" />
                    <p className="text-[10px] text-muted-foreground">Si renseigné, seules les vidéos contenant ces mots seront aspirées.</p>
                  </div>
                  <DialogFooter className="mt-4">
                    <Button type="button" variant="outline" onClick={() => setIsAddSourceOpen(false)}>Annuler</Button>
                    <Button type="submit">Enregistrer</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

      </Tabs>

      <Dialog open={!!previewProg} onOpenChange={(open) => !open && setPreviewProg(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewProg?.type && getMediaIcon(previewProg.type)}
              {previewProg?.frTitle}
            </DialogTitle>
            <DialogDescription>{previewProg?.channel}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {previewProg?.posterUrl ? (
              <div className="w-full aspect-video rounded-xl overflow-hidden shadow bg-muted/20">
                 <img src={previewProg.posterUrl} alt="Affiche" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-full h-32 bg-muted rounded-xl flex items-center justify-center text-muted-foreground border-2 border-dashed">
                 Aucune affiche disponible
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4 text-sm mt-4">
              <div>
                <span className="font-semibold text-muted-foreground">Date :</span> {previewProg?.dateFormatted_fr} à {previewProg?.time}
              </div>
              <div>
                <span className="font-semibold text-muted-foreground">Animé par :</span> {previewProg?.host_fr || "Non renseigné"}
              </div>
            </div>

            <div>
              <span className="font-semibold text-muted-foreground block mb-1">Description :</span>
              <p className="text-sm bg-muted/30 p-3 rounded-lg border">{previewProg?.description_fr || "Aucune description."}</p>
            </div>

            <div className="flex gap-2 pt-2">
              {previewProg?.liveUrl && (
                <Button variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100" asChild>
                  <a href={previewProg.liveUrl} target="_blank" rel="noreferrer">Voir le Live</a>
                </Button>
              )}
              {previewProg?.replayUrl && (
                <Button variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100" asChild>
                  <a href={previewProg.replayUrl} target="_blank" rel="noreferrer">Replay VOD</a>
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
             <Button onClick={() => setPreviewProg(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmissionsPage;
